import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, signal, input, output, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NoRecordsComponent } from '../../../../shared/components/no-records/no-records.component';
import { ActionType } from '../../../../shared/enums/common';
import { RideStatusType } from '../../../../shared/enums/rideStatusType';
import { RideDocumentDto } from '../../../../shared/interfaces/ride-firestore';
import { FiltersService } from '../../../../shared/services/filters.service';
import { FireStoreService } from '../../../../shared/services/firestore.service';
import { RequestBuilderService } from '../../../../shared/services/request-builder.service';
import { StateService } from '../../../../shared/services/state.service';
import { DocumentData, QueryConstraint, where } from 'firebase/firestore';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Scroller, ScrollerModule } from 'primeng/scroller';
import { SkeletonModule } from 'primeng/skeleton';
import { debounceTime, filter, fromEvent, Subscription } from 'rxjs';
import { LoaderService } from '../../../../core/components/loader/loader.service';
import { ActionDialogService } from '../../services/action-dialog.service';
import { RideItemComponent } from '../ride-item/ride-item.component';
import { DriverDetailsCacheService } from '../../services/driver-details-cache.service';
import { EnvConfigService } from '../../../../shared/services/env-config.service';
import { CameraStatusService } from '../../services/camera-status.service';
import { UserRole } from '../../../../../app/core/auth/enums/userRoles';
import { AuthService } from '../../../../../app/core/auth/services/auth.service';

@Component({
  selector: 'app-ride-list-item',
  standalone: true,
  imports: [CommonModule, TableModule, ScrollingModule, RideItemComponent, ScrollerModule, InputTextModule, FormsModule, NoRecordsComponent, SkeletonModule],
  templateUrl: './ride-list-item.component.html',
  styleUrl: './ride-list-item.component.scss',
})
export class RideListItemComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly firestoreService = inject(FireStoreService);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly filtersService = inject(FiltersService);
  private readonly actionDialogService = inject(ActionDialogService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly driverDetailsCacheService = inject(DriverDetailsCacheService);
  private readonly env = inject(EnvConfigService);
  private readonly cameraStatusService = inject(CameraStatusService);

  private readonly loaderService = inject(LoaderService);
  private readonly authService = inject(AuthService);
  loaderState$ = this.loaderService.loaderState;

  readonly view = input<'rides-list' | 'ride-details' | 'rides-list-map-view'>('rides-list');
  readonly activeRideId = input<number | undefined>();
  appliedFilters: { field: string; operator: any; value: string; ignoreCase: string }[] = [];
  rides = signal<any[]>([]);
  readonly pVirtualScroller = viewChild.required<Scroller>('pVirtualScroller');
  appSetting = this.authService.appSetting();

  searchAllText: string = '';
  allItemsLoaded = false;
  firstTime = signal<boolean>(true);
  isLoadingMore = signal<boolean>(false); // For skeleton loader

  FSRidesSubscriptions: Subscription[] = [];
  latestQueryConstraints: QueryConstraint[][] = [];
  currentRole = (localStorage.getItem('role') ?? '').toLowerCase();
  userRole = UserRole;

  batch = 50; // Display 50 items per page
  firestoreBatchSize = 10; // Firestore 'in' query limit
  totalItems = 0;
  private readonly ridesMap = new Map();
  private FilterSubscription!: Subscription;
  reFetchRidesSubscription!: Subscription;
  statistics = signal<{ name: string; value: number; color: string }[]>([]);

  clientSideCurrentPage = 1;
  clientSideTotalPages = 0;
  filteredRideIds: number[] = [];

  private scrollSubscription: Subscription | null = null;
  private scrollListenerSetup = false; // Track if scroll listener has been setup

  readonly ridesChange = output<number[]>();

  loading = false;
  isPendingRides = false;
  pendingRidesCount = 0;
  private isPendingRidesInitialized = false;
  private reorderSubscription!: Subscription;

  // Inject stateService at class field level so it's available for toObservable
  private readonly stateService = inject(StateService);

  // Convert reorderRideToTop signal to observable for subscription
  private readonly reorderRideToTop$ = toObservable(this.stateService.reorderRideToTop);

  ngOnInit() {
    // Listen for reorder signal (e.g., after unassigning driver)
    this.reorderSubscription = this.reorderRideToTop$.pipe(filter((rideId): rideId is number => rideId !== null)).subscribe(rideId => {
      this.moveRideToTop(rideId);
      this.stateService.setReorderRideToTop(null);
    });

    this.listenOnVehicleCameraStatuses();

    // Fetch pending rides count only on the very first load
    if (this.firstTime()) {
      this.fetchPendingRidesCount();
    }

    // Subscribe to pending rides state
    this.isPendingRides = this.stateService.getIsPendingRides();
    this.stateService.isPendingRides$.subscribe(newPendingRidesState => {
      this.handlePendingRidesStateChange(newPendingRidesState);
    });

    if (!this.stateService.shouldPreserveState()) {
      this.filtersService.triggerFilterAction(null);
      this.stateService.setDisplayedRides([]);
      this.filteredRideIds = [];
      this.stateService.setFilteredIds([]);
    }
    this.filteredRideIds = this.stateService.getFilteredIds();
    this.clientSideTotalPages = Math.ceil(this.filteredRideIds.length / this.batch);

    if (this.stateService.getSavedQueryConstraints()?.length > 0) {
      this.latestQueryConstraints = this.stateService.getSavedQueryConstraints();

      this.stateService.getSavedQueryConstraints().forEach(queryConstraints => {
        this.FSRidesSubscriptions.push(
          this.firestoreService.getRidesData(this.env.getConfig().ridesCollection, this.batch, queryConstraints).subscribe((res: any) => {
            this.handleFSLiveUpdates(res.changes, 'listen');
          }),
        );
      });
    }

    this.reFetchRidesSubscription = this.actionDialogService.reFetchRides$.subscribe((shouldFetchData: boolean) => {
      if (shouldFetchData) {
        this.getRidesFromFS(true);
      }
    });
    this.FilterSubscription = this.filtersService.filterTriggered$.subscribe(data => {
      // clear driver availability cache to update the driver availability status on filter
      this.driverDetailsCacheService.resetAvailabilityCache();

      this.search(data);
    });
  }

  ngAfterViewInit() {
    this.pVirtualScroller()
      ?.onScrollIndexChange.pipe(debounceTime(200))
      .subscribe(res => {
        res.first == 0 ? this.saveLatestScrollIndex(1) : this.saveLatestScrollIndex(res.first + 4);
      });

    // Setup scroll listener after view is initialized (only if not already setup)
    // This ensures it's always active even when navigating back to the component
    if (!this.scrollListenerSetup) {
      setTimeout(() => {
        this.setupScrollListener();
      }, 500);
    }

    if (this.activeRideId()) {
      setTimeout(() => {
        const activeRideIndex = this.rides().findIndex(r => r.original_ride_id == this.activeRideId());
        this.pVirtualScroller().scrollToIndex(activeRideIndex, 'smooth');
      }, 500);
      return;
    }

    const latestScrollIndex = this.stateService.getRidesLatestScrollIndex();

    if (latestScrollIndex && !this.activeRideId()) {
      setTimeout(() => {
        this.pVirtualScroller().scrollToIndex(latestScrollIndex, 'smooth');
      }, 500);
    }
  }

  listenOnVehicleCameraStatuses() {
    this.firestoreService.listenToCollectionChanges(this.env.getConfig().cameraStatusesCollection).subscribe({
      next: (res: any) => {
        const { changes } = res;
        console.log(changes);
        changes.forEach((change: { id: string; data: any; type: string }) => {
          change.data.last_sync_date = change.data.last_sync_date?.toDate();
          this.cameraStatusService.updateStatus(change.data.partner_id, change.data);
          //hint: send updated status to ride items if any
        });
      },
      error: err => {
        console.log(err);
      },
    });
  }

  private handlePendingRidesStateChange(newPendingRidesState: boolean) {
    const hasStateChanged = this.isPendingRidesInitialized && this.isPendingRides !== newPendingRidesState;

    this.isPendingRides = newPendingRidesState;

    if (hasStateChanged) {
      this.getRidesFromFS(true);
    } else {
      this.isPendingRidesInitialized = true;
    }
  }

  setupScrollListener() {
    // Clean up existing subscription if any
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
      this.scrollSubscription = null;
      this.scrollListenerSetup = false; // Reset flag when cleaning up
    }

    // Get the scrollable element from PrimeNG's virtualScroller (PrimeNG v21 root class is .p-virtualscroller, was .p-virtualscroller-list pre-migration)
    const scrollableElement = this.pVirtualScroller()?.el?.nativeElement.querySelector('.p-virtualscroller');

    if (!scrollableElement) {
      console.error('Could not find scrollable element');
      return;
    }

    // Add scroll event listener
    this.scrollSubscription = fromEvent(scrollableElement, 'scroll')
      .pipe(debounceTime(200)) // Prevent too many events
      .subscribe(e => {
        this.checkScrollPosition(scrollableElement);
      });
  }

  checkScrollPosition(element: HTMLElement) {
    // Don't trigger if we're already loading or all items are loaded
    if (this.allItemsLoaded || this.loading) {
      return;
    }

    // Calculate scroll position
    const scrollPosition = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    // Calculate how close to the bottom we are (as a percentage)
    const scrollPercentage = (scrollPosition + clientHeight) / scrollHeight;

    // If we're close to the bottom (e.g., within 30% of the total height)
    if (scrollPercentage > 0.7) {
      this.loadMoreRides2();
    }
  }

  saveLatestScrollIndex(index: number) {
    this.stateService.setRidesLatestScrollIndex(index);
  }

  /**
   * Moves a ride to the top of the list (e.g., after unassigning driver)
   */
  private moveRideToTop(rideId: number): void {
    const currentRides = this.rides();
    const rideIndex = currentRides.findIndex(r => r.original_ride_id === rideId);

    if (rideIndex > 0) {
      const [ride] = currentRides.splice(rideIndex, 1);
      currentRides.unshift(ride);
      this.rides.set([...currentRides]);
      this.stateService.setDisplayedRides(this.rides());
    }
  }

  reset() {
    this.appliedFilters = [];
    this.rides.set([]);
    this.ridesMap.clear();
    this.stateService.setDisplayedRides([]);

    this.filteredRideIds = [];
    this.stateService.setFilteredIds([]);
    this.clientSideCurrentPage = 1;
    this.clientSideTotalPages = 0;
  }

  search(e: any) {
    if (!this.firstTime()) {
      this.reset();
    }
    this.appliedFilters = e.map((x: any) => x);
    this.searchAllText = e.searchAllText;
    this.getRidesFromFS();
  }

  private async createRequestBody(isPendingRidesOnly: boolean, includeAppliedFilters: boolean = true) {
    await this.filtersService.setDateOfService();
    const dotFilter = {
      field: `dateOfService`,
      operator: `Equals`,
      value: localStorage.getItem('serviceDate'),
      ignoreCase: '',
    };

    // Determine the pending rides filter value
    const pendingRidesValue = isPendingRidesOnly || this.isPendingRides ? 'true' : 'false';

    const pendingRidesFilter = {
      field: `FilterPendingRides`,
      operator: `Equals`,
      value: pendingRidesValue,
      ignoreCase: '',
    };

    const filters = includeAppliedFilters
      ? [
          ...this.appliedFilters.map(f => ({
            field: f.field,
            operator: f.operator,
            value: f.value,
            ignoreCase: f.ignoreCase,
          })),
          dotFilter,
          pendingRidesFilter,
        ]
      : [dotFilter, pendingRidesFilter];

    return {
      elasticExpression: { filters },
      searchAllText: includeAppliedFilters ? this.searchAllText?.toString() || '' : '',
    };
  }

  async fetchPendingRidesCount() {
    const body = await this.createRequestBody(true, false);

    this.requestBuilder.post(`rides/filtered-ids`, body).subscribe({
      next: res => {
        this.pendingRidesCount = res.data.ids?.length ?? 0;
        this.stateService.setPendingRidesCount(this.pendingRidesCount);
      },
      error: err => {
        console.error('Error fetching pending rides count:', err);
      },
    });
  }

  async getRidesFromFS(resetRide = false) {
    if (this.firstTime() && this.stateService.getDisplayedRides()?.length > 0 && this.stateService.shouldPreserveState()) {
      this.rides.set(this.stateService.getDisplayedRides());
      this.rides().forEach(ride => {
        this.ridesMap.set(ride['original_ride_id'], ride.original_ride_id);
      });

      this.clientSideCurrentPage = Math.ceil(this.rides().length / this.batch) + 1;

      this.firstTime.set(false);
      return;
    }

    const body = await this.createRequestBody(false, true);

    console.log('Body: ', body);

    this.firstTime.set(false);
    if (resetRide) {
      this.filteredRideIds = [];
      this.stateService.setFilteredIds([]);
      this.clientSideCurrentPage = 1;
      this.clientSideTotalPages = 0;

      this.rides.set([]);
      this.ridesMap.clear();
      this.stateService.setDisplayedRides([]);

      if (this.env.getConfig().isRunAsLibrary) {
        this.router.navigateByUrl(`/dispatch/monitoring-center`);
      } else {
        this.router.navigateByUrl(`/monitoring-center`);
      }
    }

    if (this.filteredRideIds.length > 0) {
      if (this.clientSideCurrentPage > this.clientSideTotalPages) {
        this.allItemsLoaded = true;
        return;
      }
      this.allItemsLoaded = false; // Reset flag when loading more items
      this.addFSFilteredBatchSubscription(body);
    } else {
      this.FSRidesSubscriptions.forEach(s => {
        s.unsubscribe();
      });
      this.FSRidesSubscriptions = [];
      this.latestQueryConstraints = [];

      this.requestBuilder.post(`rides/filtered-ids`, body).subscribe({
        next: res => {
          this.getStatistics(body);
          this.filteredRideIds = res.data.ids ?? [];
          this.stateService.setFilteredIds(this.filteredRideIds);
          this.ridesChange.emit(this.filteredRideIds);

          // Update pending rides count ONLY when fetching pending rides WITHOUT any applied filters
          // This prevents overwriting the accurate count with a filtered subset count
          if (this.isPendingRides && this.appliedFilters.length === 0 && !this.searchAllText) {
            this.pendingRidesCount = this.filteredRideIds.length;
            this.stateService.setPendingRidesCount(this.pendingRidesCount);
          }

          this.clientSideTotalPages = Math.ceil(this.filteredRideIds.length / this.batch);
          this.clientSideCurrentPage = 1;
          this.addFSFilteredBatchSubscription(body);
        },
        error: err => {
          console.error('Error fetching filtered ride ids:', err);
        },
      });
    }
  }

  private addFSFilteredBatchSubscription(body: any) {
    this.loading = true;
    this.isLoadingMore.set(true);
    const start = this.clientSideCurrentPage == 1 ? 0 : this.clientSideCurrentPage * this.batch - this.batch;
    const end = this.clientSideCurrentPage * this.batch;
    const rideIdsBatch = this.filteredRideIds?.slice(start, end);

    if (rideIdsBatch?.length > 0) {
      // Split into chunks of 10 for Firestore 'in' query limit
      const chunks = this.chunkArray(rideIdsBatch, this.firestoreBatchSize);

      // Track items received per chunk to handle Firestore's split emissions
      const chunkItemCounts = new Map<number, number>(); // chunkIndex -> item count
      let pageIncrementDone = false; // Prevent multiple increments

      chunks.forEach((chunk, chunkIndex) => {
        chunkItemCounts.set(chunkIndex, 0);
        let queryConstraints: QueryConstraint[] = [where('id', 'in', chunk)];

        this.latestQueryConstraints.push(queryConstraints);
        this.FSRidesSubscriptions.push(
          this.firestoreService.getRidesData(this.env.getConfig().ridesCollection, this.firestoreBatchSize, queryConstraints).subscribe({
            next: (res: any) => {
              console.log(res);

              if (res.newItems.length > 0) {
                this.setRides(res.newItems, body, rideIdsBatch);

                // Update count for this chunk
                chunkItemCounts.set(chunkIndex, (chunkItemCounts.get(chunkIndex) ?? 0) + res.newItems.length);

                // Setup scroll listener after first batch is rendered (only once)
                if (this.clientSideCurrentPage === 1 && !this.scrollListenerSetup) {
                  this.scrollListenerSetup = true;
                  setTimeout(() => {
                    this.setupScrollListener();
                  }, 500);
                }
              }

              // Calculate total items received so far for this page
              const totalReceivedItems = Array.from(chunkItemCounts.values()).reduce((sum, count) => sum + count, 0);
              const expectedItemsForPage = rideIdsBatch.length; // Actual items expected (may be less than batch size on last page)

              // Check if we've received all expected items for this page
              // This handles both full pages and partial last pages
              if (totalReceivedItems >= expectedItemsForPage && !pageIncrementDone) {
                pageIncrementDone = true;
                this.loading = false;
                this.isLoadingMore.set(false);

                // Check if this is a full page or last partial page
                const isFullPage = this.filteredRideIds.length / this.batch >= this.clientSideCurrentPage;

                if (isFullPage) {
                  // Full page - only increment if we got the complete batch
                  if (totalReceivedItems === this.batch) {
                    this.clientSideCurrentPage += 1;
                    console.log('Current Page increased (full page): ', this.clientSideCurrentPage);
                  }
                } else {
                  // Last page (partial) - increment regardless of size
                  this.clientSideCurrentPage += 1;
                  console.log('Current Page increased (last page): ', this.clientSideCurrentPage);
                }

                if (this.clientSideCurrentPage > this.clientSideTotalPages) {
                  this.allItemsLoaded = true;
                } else {
                  this.allItemsLoaded = false;
                }
              }

              this.handleFSLiveUpdates(res.changes, 'filters');
            },
            error: err => {
              console.log(err);
              this.loading = false;
              this.isLoadingMore.set(false);
              // Mark this chunk as complete with whatever we got
              chunkItemCounts.set(chunkIndex, chunk.length);
            },
          }),
        );
      });
    } else {
      this.setRides([]);
      this.loading = false;
      this.isLoadingMore.set(false);
    }
  }

  private chunkArray(array: any[], chunkSize: number): any[][] {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  private handleFSLiveUpdates(changes: { id: string; data: RideDocumentDto; type: string; doc: DocumentData }[], firingApproach?: string) {
    changes.forEach(c => {
      let fetchedRideOriginalId = this.ridesMap.get(c.data.original_ride_id);
      if (fetchedRideOriginalId) {
        let gridRides = [...this.rides()];
        let changedRide = gridRides.find(b => b.original_ride_id == fetchedRideOriginalId);
        if (c.type == ActionType.modified) {
          if (c.data.ride_status_id == RideStatusType.Deleted) {
            // call get filteredIds
            this.getRidesFromFS(true);
          } else if (this.isPendingRides && (c.data.ride_status_id == RideStatusType.Completed || c.data.ride_status_id == RideStatusType.NoShow)) {
            // Refresh the list when a ride is completed or marked as no-show in pending rides view
            this.getRidesFromFS(true);
          } else if (this.currentRole == UserRole.Provider && c.data.index_organization_id.toString() != (localStorage.getItem('consumer_identity') ?? '0')) {
            gridRides.splice(gridRides.indexOf(changedRide), 1); // remove from list
            this.rides.set(gridRides);
            this.ridesMap.delete(c.data.original_ride_id);
            this.stateService.setDisplayedRides(this.rides());
            this.cdr.detectChanges();
          } else {
            gridRides.splice(gridRides.indexOf(changedRide), 1, c.data);
            this.rides.set(gridRides);
            this.stateService.setDisplayedRides(this.rides());
            this.cdr.detectChanges();
          }
        } else if (c.type == ActionType.removed) {
          gridRides.splice(gridRides.indexOf(changedRide), 1);
          this.ridesMap.delete(c.data.original_ride_id);
          this.rides.set(gridRides);
          this.stateService.setDisplayedRides(this.rides());
        }
      }
    });
  }

  private setRides(rideDocumnets: RideDocumentDto[], statisticsBody?: any, rideIds?: number[]) {
    let sortedRides = rideDocumnets;
    if (rideIds) {
      sortedRides = [...rideDocumnets].sort((a, b) => {
        const indexA = rideIds.indexOf(a.id);
        const indexB = rideIds.indexOf(b.id);
        return indexA - indexB;
      });
    }

    this.rides.update(rides => [
      ...rides,
      ...sortedRides.filter((r: RideDocumentDto) => {
        if (!this.ridesMap.get(r.original_ride_id)) {
          this.ridesMap.set(r['original_ride_id'], r.original_ride_id);
          return true;
        }
        return false;
      }),
    ]);

    this.stateService.setDisplayedRides(this.rides());
  }

  getStatistics(body: any) {
    this.statistics.set([]);
    this.stateService.setStatistics([]);

    this.requestBuilder.post(`rides/statistics`, body).subscribe(res => {
      this.statistics.set([]);
      this.stateService.setStatistics([]);

      Object.entries(res.data).forEach(([key, value]) => {
        let color: string;

        switch (key) {
          case 'scheduled':
            color = 'default';
            break;
          case 'inProgress':
            color = 'warning';
            break;
          case 'completed':
            color = 'success';
            break;
          case 'lateCanceled':
          case 'noShow':
            color = 'secondary';
            break;
          case 'attention':
            color = 'error';
            break;
          default:
            color = 'default';
        }

        if (key != `earlyCanceled` && key != `holidays`) {
          this.statistics.update(prev => [
            ...prev,
            {
              name: key,
              value: Number(value),
              color,
            },
          ]);
        }
      });
      this.statistics.update(prev => [
        ...prev,
        {
          name: `Early Cancel + Holiday`,
          value: res.data.earlyCanceled + res.data.holidays,
          color: 'info',
        },
      ]);

      this.stateService.setStatistics(this.statistics());
    });
  }

  loadMoreRides2(): void {
    if (this.allItemsLoaded || this.loading) {
      return;
    }
    this.getRidesFromFS();
  }

  ngOnDestroy(): void {
    this.FilterSubscription?.unsubscribe();
    this.reFetchRidesSubscription?.unsubscribe();
    this.reorderSubscription?.unsubscribe();
    this.scrollSubscription?.unsubscribe();
    this.scrollListenerSetup = false; // Reset flag on destroy
    this.FSRidesSubscriptions.forEach(s => {
      s.unsubscribe();
    });
    this.stateService.saveQueryConstraints(this.latestQueryConstraints);
  }
}
